<template>
  <div class="app-container customer-analysis-page">
    <el-card shadow="never" class="filter-card">
      <div slot="header" class="page-header">
        <div>
          <div class="page-title">客户经营分析</div>
          <div class="page-subtitle">识别重点客户、经营风险和下一步行动；金额口径按所选统计周期，风险口径按当前状态。</div>
        </div>
        <div class="generated-time" v-if="overview.generatedTime">分析时间：{{ overview.generatedTime }}</div>
      </div>
      <el-form ref="queryForm" :model="queryParams" label-width="82px">
        <el-row :gutter="12">
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="客户名称" prop="customerName">
              <el-input v-model="queryParams.customerName" clearable placeholder="请输入客户名称" @keyup.enter.native="handleQuery"/>
            </el-form-item>
          </el-col>
          <dept-user-select
            ref="managerSelect"
            :form-param="queryParams"
            :dept-multiple="false"
            :user-multiple="false"
            :auto-select-user="false"
            dept-id-field="managerDeptId"
            user-id-field="managerId"
          />
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="统计周期" prop="statDate">
              <el-date-picker
                v-model="queryParams.statDate"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                :clearable="false"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="经营分层" prop="segment">
              <el-select v-model="queryParams.segment" clearable placeholder="全部分层" style="width:100%">
                <el-option v-for="item in segmentOptions" :key="item.value" :label="item.label" :value="item.value"/>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="风险类型" prop="riskType">
              <el-select v-model="queryParams.riskType" clearable placeholder="全部风险" style="width:100%">
                <el-option v-for="item in riskOptions" :key="item.value" :label="item.label" :value="item.value"/>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="客户性质" prop="customerNature">
              <el-select v-model="queryParams.customerNature" clearable placeholder="全部性质" style="width:100%">
                <el-option v-for="item in natureOptions" :key="item.dictValue" :label="item.dictLabel" :value="item.dictValue"/>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="客户类型" prop="customerType">
              <el-select v-model="queryParams.customerType" clearable placeholder="全部类型" style="width:100%">
                <el-option v-for="item in typeOptions" :key="item.dictValue" :label="item.dictLabel" :value="item.dictValue"/>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="客户级别" prop="customerLevel">
              <el-select v-model="queryParams.customerLevel" clearable placeholder="全部级别" style="width:100%">
                <el-option v-for="item in levelOptions" :key="item.dictValue" :label="item.dictLabel" :value="item.dictValue"/>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="24" class="filter-actions">
            <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <el-row :gutter="12" class="summary-row" v-loading="overviewLoading">
      <el-col v-for="card in summaryCards" :key="card.label" :xs="12" :sm="8" :lg="4">
        <el-card shadow="hover" class="summary-card">
          <div class="summary-label">{{ card.label }}</div>
          <div class="summary-value" :class="card.className">{{ card.value }}</div>
          <div class="summary-note">{{ card.note }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12" class="analysis-row">
      <el-col :xs="24" :lg="16">
        <el-card shadow="never" v-loading="overviewLoading">
          <div slot="header" class="card-header">
            <span>客户价值—健康矩阵</span>
            <span class="card-hint">气泡越大，增长潜力越高；红色为高风险客户</span>
          </div>
          <customer-analysis-matrix :data-list="overview.matrixList || []" @select="openCustomer"/>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="segment-card" v-loading="overviewLoading">
          <div slot="header" class="card-header"><span>客户分层</span></div>
          <div v-for="item in segmentDistribution" :key="item.name" class="segment-row">
            <span class="segment-name">{{ item.name }}</span>
            <el-progress :percentage="item.percentage" :show-text="false" :stroke-width="10"/>
            <span class="segment-count">{{ item.count }} 家</span>
          </div>
          <div class="score-explain">
            <div>价值分：合同、回款、复购、产品广度</div>
            <div>健康分：跟进、逾期、服务、交付、联系人</div>
            <div>潜力分：在途商机金额、数量和近期新增</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="risk-card" v-loading="overviewLoading">
      <div slot="header" class="card-header">
        <span>优先行动清单</span>
        <span class="card-hint">先处理高价值、高风险且有明确责任人的客户</span>
      </div>
      <el-table :data="overview.topRiskCustomers || []" empty-text="当前筛选范围内暂无风险客户">
        <el-table-column label="客户" min-width="170">
          <template slot-scope="scope">
            <el-link type="primary" :underline="false" @click="openCustomer(scope.row.customerId)">{{ scope.row.customerName }}</el-link>
          </template>
        </el-table-column>
        <el-table-column label="负责人" width="100" prop="managerName"/>
        <el-table-column label="分层" width="120" prop="segmentName"/>
        <el-table-column label="风险" min-width="220">
          <template slot-scope="scope">
            <el-tag v-for="risk in scope.row.riskList" :key="risk.type" :type="riskTagType(risk.level)" size="mini" class="risk-tag">{{ risk.name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="逾期回款" width="130" align="right">
          <template slot-scope="scope">{{ money(scope.row.overdueAmount) }}</template>
        </el-table-column>
        <el-table-column label="建议动作" min-width="260" prop="nextAction"/>
      </el-table>
    </el-card>

    <el-card shadow="never" class="list-card">
      <div slot="header" class="card-header">
        <span>客户分析明细</span>
        <el-select v-model="queryParams.sortBy" size="small" style="width:150px" @change="loadList">
          <el-option label="风险优先" value="RISK"/>
          <el-option label="客户价值" value="VALUE"/>
          <el-option label="增长潜力" value="GROWTH"/>
          <el-option label="合同金额" value="CONTRACT"/>
          <el-option label="逾期金额" value="OVERDUE"/>
        </el-select>
      </div>
      <el-table v-loading="listLoading" :data="dataList">
        <el-table-column label="客户名称" min-width="170" fixed="left">
          <template slot-scope="scope">
            <el-link type="primary" :underline="false" @click="openCustomer(scope.row.customerId)">{{ scope.row.customerName }}</el-link>
          </template>
        </el-table-column>
        <el-table-column label="负责人" width="100" prop="managerName"/>
        <el-table-column label="经营分层" width="120">
          <template slot-scope="scope"><el-tag size="mini" effect="plain">{{ scope.row.segmentName }}</el-tag></template>
        </el-table-column>
        <el-table-column label="价值" width="70" align="center" prop="valueScore"/>
        <el-table-column label="健康" width="70" align="center">
          <template slot-scope="scope"><span :class="scoreClass(scope.row.healthScore)">{{ scope.row.healthScore }}</span></template>
        </el-table-column>
        <el-table-column label="潜力" width="70" align="center" prop="growthScore"/>
        <el-table-column label="合同额" width="130" align="right">
          <template slot-scope="scope">{{ money(scope.row.totalContractAmount) }}</template>
        </el-table-column>
        <el-table-column label="已回款" width="130" align="right">
          <template slot-scope="scope">{{ money(scope.row.receivedAmount) }}</template>
        </el-table-column>
        <el-table-column label="逾期" width="130" align="right">
          <template slot-scope="scope"><span class="danger-text">{{ money(scope.row.overdueAmount) }}</span></template>
        </el-table-column>
        <el-table-column label="在途商机" width="100" align="center" prop="openBusinessCount"/>
        <el-table-column label="未跟进天数" width="110" align="center">
          <template slot-scope="scope">{{ scope.row.lastFollowDays == null ? '暂无记录' : scope.row.lastFollowDays + '天' }}</template>
        </el-table-column>
        <el-table-column label="数据完整度" width="110" align="center">
          <template slot-scope="scope">{{ scope.row.dataCompleteness }}%</template>
        </el-table-column>
        <el-table-column label="下一步动作" min-width="260" prop="nextAction"/>
      </el-table>
      <pagination v-show="total > 0" :total="total" :page.sync="queryParams.pageNum" :limit.sync="queryParams.pageSize" @pagination="loadList"/>
    </el-card>
  </div>
</template>

<script>
import deptUserSelect from '@/views/crm/components/deptUserSelect'
import CustomerAnalysisMatrix from './components/CustomerAnalysisMatrix'
import { listCustomerAnalysis, queryCustomerAnalysisOverview } from '@/api/crm/customerAnalysis'
import { amountFormat } from '@/utils/index'

function formatDateValue(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function createDefaultQueryParams() {
  const end = new Date()
  const start = new Date()
  start.setFullYear(start.getFullYear() - 1)
  start.setDate(start.getDate() + 1)
  return {
    pageNum: 1,
    pageSize: 20,
    customerName: null,
    managerDeptId: null,
    managerId: null,
    customerNature: null,
    customerType: null,
    customerLevel: null,
    segment: null,
    riskType: null,
    sortBy: 'RISK',
    statDate: [formatDateValue(start), formatDateValue(end)]
  }
}

export default {
  name: 'CustomerAnalysis',
  components: { deptUserSelect, CustomerAnalysisMatrix },
  data() {
    return {
      overviewLoading: false,
      listLoading: false,
      overview: { summary: {}, matrixList: [], topRiskCustomers: [] },
      dataList: [],
      total: 0,
      natureOptions: [],
      typeOptions: [],
      levelOptions: [],
      queryParams: createDefaultQueryParams(),
      segmentOptions: [
        { label: '核心稳定客户', value: 'CORE' },
        { label: '重点挽回客户', value: 'RESCUE' },
        { label: '高潜增长客户', value: 'GROWTH' },
        { label: '普通维护客户', value: 'MAINTAIN' },
        { label: '沉睡客户', value: 'DORMANT' }
      ],
      riskOptions: [
        { label: '回款逾期', value: 'OVERDUE_RECEIVABLE' },
        { label: '长期未跟进', value: 'LONG_NO_FOLLOW' },
        { label: '服务待处理', value: 'PENDING_SERVICE' },
        { label: '交付风险', value: 'DELIVERY_RISK' },
        { label: '商机停滞', value: 'STALLED_BUSINESS' },
        { label: '联系人缺失', value: 'MISSING_CONTACT' },
        { label: '负责人缺失', value: 'MISSING_MANAGER' }
      ]
    }
  },
  computed: {
    summaryCards() {
      const summary = this.overview.summary || {}
      return [
        { label: '有效客户', value: summary.customerCount || 0, note: `近30天活跃 ${summary.activeCustomerCount || 0} 家` },
        { label: '成交客户', value: summary.signedCustomerCount || 0, note: '所选周期内有已签约合同' },
        { label: '风险客户', value: summary.riskCustomerCount || 0, note: `其中高风险 ${summary.highRiskCustomerCount || 0} 家`, className: 'is-danger' },
        { label: '合同金额', value: `${this.moneyWan(summary.totalContractAmount)}万`, note: '所选周期已签约' },
        { label: '已回款', value: `${this.moneyWan(summary.receivedAmount)}万`, note: '所选周期实际回款', className: 'is-success' },
        { label: '逾期回款', value: `${this.moneyWan(summary.overdueAmount)}万`, note: `待回款 ${this.moneyWan(summary.pendingReceivableAmount)}万`, className: 'is-danger' }
      ]
    },
    segmentDistribution() {
      const summary = this.overview.summary || {}
      const total = Number(summary.customerCount || 0)
      const counts = summary.segmentCount || {}
      return Object.keys(counts).map(name => ({
        name,
        count: Number(counts[name] || 0),
        percentage: total ? Math.round(Number(counts[name] || 0) * 100 / total) : 0
      }))
    }
  },
  created() {
    this.loadDicts()
    this.loadAll()
  },
  methods: {
    defaultQueryParams() {
      return createDefaultQueryParams()
    },
    loadDicts() {
      this.getDicts('customer_nature').then(response => { this.natureOptions = response.data || [] })
      this.getDicts('sys_customer_type').then(response => { this.typeOptions = response.data || [] })
      this.getDicts('sys_customer_level').then(response => { this.levelOptions = response.data || [] })
    },
    buildRequest() {
      const params = Object.assign({}, this.queryParams)
      params.startDate = params.statDate && params.statDate.length ? params.statDate[0] : null
      params.endDate = params.statDate && params.statDate.length ? params.statDate[1] : null
      delete params.statDate
      delete params.managerDeptId
      return params
    },
    loadAll() {
      this.loadOverview()
      this.loadList()
    },
    loadOverview() {
      this.overviewLoading = true
      queryCustomerAnalysisOverview(this.buildRequest()).then(response => {
        this.overview = response.data || { summary: {}, matrixList: [], topRiskCustomers: [] }
      }).finally(() => {
        this.overviewLoading = false
      })
    },
    loadList() {
      this.listLoading = true
      listCustomerAnalysis(this.buildRequest()).then(response => {
        this.dataList = response.rows || []
        this.total = Number(response.total || 0)
      }).finally(() => {
        this.listLoading = false
      })
    },
    handleQuery() {
      this.queryParams.pageNum = 1
      this.loadAll()
    },
    resetQuery() {
      this.queryParams = this.defaultQueryParams()
      this.$nextTick(() => {
        if (this.$refs.managerSelect) {
          this.$refs.managerSelect.queryUserList(null)
        }
        this.loadAll()
      })
    },
    openCustomer(customerId) {
      window.open('/crm/customer/detail/' + customerId, '_blank')
    },
    money(value) {
      return amountFormat(Number(value || 0))
    },
    moneyWan(value) {
      return amountFormat(Number(value || 0) / 10000, 2)
    },
    riskTagType(level) {
      if (level === 'HIGH') return 'danger'
      if (level === 'MEDIUM') return 'warning'
      return 'info'
    },
    scoreClass(score) {
      if (Number(score || 0) < 60) return 'danger-text'
      if (Number(score || 0) >= 80) return 'success-text'
      return ''
    }
  }
}
</script>

<style scoped>
.customer-analysis-page { background: #f5f7fa; min-height: calc(100vh - 84px); }
.filter-card, .summary-row, .analysis-row, .risk-card { margin-bottom: 14px; }
.page-header, .card-header { display: flex; justify-content: space-between; align-items: center; }
.page-title { color: #303133; font-size: 20px; font-weight: 600; }
.page-subtitle, .generated-time, .card-hint { margin-top: 6px; color: #909399; font-size: 12px; }
.filter-actions { text-align: right; }
.summary-card { min-height: 116px; }
.summary-label { color: #909399; font-size: 13px; }
.summary-value { margin: 12px 0 8px; color: #303133; font-size: 25px; font-weight: 600; }
.summary-value.is-danger, .danger-text { color: #f56c6c; }
.summary-value.is-success, .success-text { color: #67c23a; }
.summary-note { color: #a8abb2; font-size: 12px; }
.segment-card { min-height: 434px; }
.segment-row { display: grid; grid-template-columns: 110px 1fr 50px; align-items: center; gap: 10px; margin: 18px 0; }
.segment-name { color: #606266; font-size: 13px; }
.segment-count { color: #909399; font-size: 12px; text-align: right; }
.score-explain { margin-top: 24px; padding: 12px; border-radius: 4px; background: #f5f7fa; color: #909399; font-size: 12px; line-height: 22px; }
.risk-tag { margin: 2px 4px 2px 0; }
.list-card { margin-bottom: 20px; }
@media (max-width: 1200px) {
  .analysis-row > .el-col { margin-bottom: 14px; }
}
</style>
